import { Capacitor } from "@capacitor/core";

// Serial queue for native SYSTEM prompts (ATT → Declared Age Range → UMP consent → push permission) so two
// iOS/Android permission dialogs never compete on screen. Web/PWA: runs immediately.
const PRIORITY = { att: 0, age: 1, consent: 2, push: 3 };
export const START_DELAY_MS = 1000;
export const GAP_MS = 700;

let queue = [];
let pumping = false;
let lastEndedAt = 0;   // fin du dernier dialogue système (ATT / UMP / push) — garde anti-enchaînement
const listeners = new Set();
// Lot 589 — « retenues » : tant qu'une raison est posée (profil à compléter, feuille ouverte…), la file attend
// avant de lancer le dialogue système suivant ; rien n'est perdu, tout repart à la levée.
const holds = new Set();
const releaseWaiters = new Set();

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const notify = () => listeners.forEach((cb) => { try { cb(pumping); } catch { /* noop */ } });

const whenVisible = () => new Promise((resolve) => {
  if (typeof document === "undefined" || document.visibilityState !== "hidden") return resolve();
  const onVis = () => { if (document.visibilityState !== "hidden") { document.removeEventListener("visibilitychange", onVis); resolve(); } };
  document.addEventListener("visibilitychange", onVis);
});

const whenReleased = () => new Promise((resolve) => {
  if (!holds.size) return resolve();
  releaseWaiters.add(resolve);
});

export function holdNativePrompts(reason) { holds.add(reason); }
export function releaseNativePrompts(reason) {
  holds.delete(reason);
  if (!holds.size) { releaseWaiters.forEach((r) => r()); releaseWaiters.clear(); }
}
export const nativePromptsHeld = () => holds.size > 0;

async function pump() {
  while (queue.length) {
    queue.sort((a, b) => a.priority - b.priority);
    const item = queue.shift();
    await whenVisible();
    await whenReleased();
    await whenVisible();
    try { item.resolve(await item.fn()); } catch (e) { item.reject(e); }
    lastEndedAt = Date.now();
    if (queue.length) await sleep(GAP_MS);
  }
  pumping = false;
  notify();
}

export function runNativePrompt(kind, fn) {
  if (!Capacitor.isNativePlatform()) return Promise.resolve().then(fn);
  return new Promise((resolve, reject) => {
    queue.push({ priority: PRIORITY[kind] ?? 9, fn, resolve, reject });
    if (!pumping) {
      pumping = true;
      notify();
      setTimeout(pump, START_DELAY_MS);
    }
  });
}

export const nativePromptsBusy = () => pumping;

// Horodatage (ms) de la fin du dernier dialogue de la file ; 0 si aucun dans cette session.
export const lastNativePromptEndedAt = () => lastEndedAt;

// cb(busy) called right away and on every change; returns unsubscribe.
export function subscribeNativePrompts(cb) {
  listeners.add(cb);
  cb(pumping);
  return () => listeners.delete(cb);
}
